//dependencies
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

//service contracts
import {
	EmployeeCustomFieldUpdate,
	EmployeeCustomFieldUpdateJSON,
	GetEmployeeDetailResponse,
	GetEmployeeDetailsHistoryResponse,
	GetEmployeeDetailsResponseJSON,
} from "@performyard/service-contracts";

import { employeeCustomFieldsQuery } from "../../../api/queries/defs/custom-fields";
import {
	deleteEmployeeAvatar,
	deleteEmployeeCustomFieldHistory,
	editEmployeeActivation,
	getEmployeeCustomField,
	getEmployeeCustomFieldHistory,
	patchEmployeeBulkCustomFields,
	patchEmployeeCustomFields,
	postEmployeeAvatar,
} from "../../../api/services/defs/internal-api";

import { cropImageToCenterRange } from "../../../utils/CropImage";
import avatarURL from "../../../utils/avatarURL";

import { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";
import { useCurrentUser } from "./auth";

export const avatarBlobUrlQueryKey = (employeeId: string) => ["avatarBlobUrl", employeeId] as const;

export const useEmployeeCustomFields = (
	employeeId: string,
	options?: PYUseQueryOptions<GetEmployeeDetailsResponseJSON>
) => {
	return useQuery<GetEmployeeDetailsResponseJSON>(employeeCustomFieldsQuery(employeeId, options));
};

export const useEmployeeCustomField = (
	employeeId: string,
	fieldId: string,
	options?: PYUseQueryOptions<GetEmployeeDetailResponse>
) => {
	return useQuery<GetEmployeeDetailResponse>({
		queryKey: ["employeeCustomFields", employeeId, fieldId],
		queryFn: async () => await getEmployeeCustomField(employeeId, fieldId),
		meta: {
			errorMessage: "There was a problem fetching the employee custom field",
		},
		...options,
	});
};

export const useEmployeeCustomFieldHistory = (
	employeeId: string,
	fieldId: string,
	options?: PYUseQueryOptions<GetEmployeeDetailsHistoryResponse>
) => {
	return useQuery<GetEmployeeDetailsHistoryResponse>({
		queryKey: ["employeeCustomFieldsHistory", employeeId, fieldId],
		queryFn: async () => await getEmployeeCustomFieldHistory(employeeId, fieldId),
		meta: {
			errorMessage: "There was a problem fetching the employee custom field history",
		},
		...options,
	});
};

export const useMutateEmployeeCustomFields = (onSuccess?: () => void, options?: PYUseMutationOptions) => {
	const queryCache = useQueryClient();
	return useMutation({
		mutationKey: ["employeeCustomFieldPatch"],
		mutationFn: async ({
			updates,
			asOf,
			employeeId,
		}: {
			updates: EmployeeCustomFieldUpdate[];
			asOf: Date;
			employeeId: string;
		}) => await patchEmployeeCustomFields(updates, employeeId, asOf),
		...options,
		onSuccess: (data, variables) => {
			onSuccess && onSuccess();
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFields", variables.employeeId] });
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFieldsHistory", variables.employeeId] });
			queryCache.invalidateQueries({ queryKey: ["emailTokens", variables.employeeId] });
			queryCache.invalidateQueries({ queryKey: ["InternalApiUser", variables.employeeId] });
		},
		meta: {
			errorMessage: "There was a problem updating the employee custom field",
		},
	});
};

export const useMutateBulkEmployeeCustomFields = (options?: PYUseMutationOptions) => {
	const queryCache = useQueryClient();
	return useMutation({
		mutationKey: ["employeeCustomFieldPatch"],
		mutationFn: async (
			updatesList: {
				updates: EmployeeCustomFieldUpdateJSON[];
				asOf: Date;
				employeeId: string;
			}[]
		) => await patchEmployeeBulkCustomFields(updatesList),
		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options?.onSuccess(data, variables, context);
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFields"] });
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFieldsHistory"] });
			queryCache.invalidateQueries({ queryKey: ["InternalApiUser"] });
		},
		meta: {
			errorMessage: "There was a problem updating the employee custom field",
		},
	});
};

export const useDeleteEmployeeCustomFieldHistory = (employeeId: string) => {
	const queryCache = useQueryClient();
	return useMutation({
		mutationKey: ["employeeCustomFieldHistoryDelete"],
		mutationFn: async (historyId: string) => await deleteEmployeeCustomFieldHistory(employeeId, historyId),
		onSuccess: () => {
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFieldsHistory", employeeId] });
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFields", employeeId] });
		},
		meta: {
			errorMessage: "There was a problem deleting the employee custom field history",
		},
	});
};

export const useDeleteEmployeeAvatar = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	const { data: currentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload?.user_id;

	return useMutation({
		mutationKey: ["employeeAvatarDelete"],
		mutationFn: async (employeeId: string) => {
			await deleteEmployeeAvatar(employeeId);
		},
		...options,
		onSuccess: async (data, employeeId, context) => {
			const oldBlobUrl = client.getQueryData<string>(avatarBlobUrlQueryKey(employeeId));
			if (oldBlobUrl?.startsWith("blob:")) URL.revokeObjectURL(oldBlobUrl);
			client.removeQueries({ queryKey: avatarBlobUrlQueryKey(employeeId) });

			// Use the /avatar/gen/ endpoint to build a default avatar URL.
			// Unlike /avatar/<id>/, the /gen/ endpoint always returns the initials SVG
			// and uses a separate CloudFront cache key, so it's unaffected by the async
			// invalidation timing of the deleted custom avatar.
			const userInfo = client.getQueryData<{ name?: string }>(["InternalApiUser", employeeId]);
			const name = userInfo?.name || "";
			const names = name.split(/\s+/).filter(Boolean);
			const initials =
				names.length >= 2
					? (names[0][0] + names[names.length - 1][0]).toUpperCase()
					: names.length === 1
						? names[0][0].toUpperCase()
						: employeeId;
			const colorIndex = [...employeeId].reduce((sum, c) => sum + c.charCodeAt(0), 0);
			const defaultAvatarUrl = avatarURL(initials, colorIndex);
			client.setQueryData(avatarBlobUrlQueryKey(employeeId), defaultAvatarUrl);

			if (employeeId === currentUserId) {
				document.querySelectorAll<HTMLImageElement>("img.user-avatar").forEach((img) => {
					img.src = defaultAvatarUrl;
				});
			}

			client.invalidateQueries({ queryKey: ["InternalApiUser", employeeId] });
			options?.onSuccess && options.onSuccess(data, employeeId, context);
		},
	});
};

export const useMutateEmployeeAvatar = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	const { data: currentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload?.user_id;

	return useMutation({
		mutationKey: ["employeeAvatarUpdate"],
		mutationFn: async ({ employeeId, file }: { employeeId: string; file: File }) => {
			const croppedFile = await cropImageToCenterRange(file, { width: 200, height: 200 });

			await postEmployeeAvatar(employeeId, croppedFile);

			return croppedFile;
		},
		...options,
		onSuccess: (croppedFile, variables, context) => {
			if (croppedFile) {
				const oldBlobUrl = client.getQueryData<string>(avatarBlobUrlQueryKey(variables.employeeId));
				if (oldBlobUrl?.startsWith("blob:")) URL.revokeObjectURL(oldBlobUrl);
				const blobUrl = URL.createObjectURL(croppedFile);
				client.setQueryData(avatarBlobUrlQueryKey(variables.employeeId), blobUrl);

				if (variables.employeeId === currentUserId) {
					document.querySelectorAll<HTMLImageElement>("img.user-avatar").forEach((img) => {
						img.src = blobUrl;
					});
				}
			}
			client.invalidateQueries({ queryKey: ["InternalApiUser", variables.employeeId] });
			options?.onSuccess && options.onSuccess(croppedFile, variables, context);
		},
	});
};

export const useMutateEmployeeActivation = (options: PYUseMutationOptions) => {
	const client = useQueryClient();
	return useMutation({
		mutationKey: ["mutateEmployeeActivation"],
		mutationFn: async ({
			employeeId,
			activate,
			termination,
			managerReassignmentId,
		}: {
			employeeId: string;
			activate: boolean;
			termination?: Date;
			managerReassignmentId?: string;
		}) => {
			await editEmployeeActivation(employeeId, activate, termination, managerReassignmentId);
		},
		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options.onSuccess(data, variables, context);

			client.invalidateQueries({ queryKey: ["employeeCustomFields", variables.employeeId] });
			client.invalidateQueries({ queryKey: ["InternalApiUser", variables.employeeId] });
		},
	});
};
